import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import { useTheme, alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PowerIcon from '@mui/icons-material/Power';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GridContainer from '../common/GridContainer';
import GridItem from '../common/GridItem';

// Shared fleet data
import { vehicles, categories, VehicleType } from '../../data/fleet';

const FleetPreview: React.FC = () => {
  const theme = useTheme();
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const filteredVehicles = activeCategory === 'All' 
    ? vehicles 
    : vehicles.filter(vehicle => vehicle.category === activeCategory);

  // Section title styling consistent with HomePage
  const sectionTitleStyles = {
    textAlign: 'center',
    mb: { xs: 6, md: 8 },
    mx: 'auto',
    maxWidth: 900
  };

  // Section subtitle styling
  const sectionSubtitleStyles = {
    color: 'secondary.main',
    fontWeight: 600,
    mb: 1,
    letterSpacing: 1,
    textTransform: 'uppercase',
    textAlign: 'center'
  };

  // Section heading styling
  const sectionHeadingStyles = {
    fontFamily: 'Playfair Display, serif',
    fontWeight: 600,
    mb: 3,
    fontSize: { xs: '2.25rem', md: '3rem' },
    textAlign: 'center'
  };

  // Section description styling
  const sectionDescriptionStyles = {
    color: 'text.secondary',
    maxWidth: 800,
    mx: 'auto',
    fontSize: { xs: '1rem', md: '1.1rem' },
    lineHeight: 1.7,
    textAlign: 'center',
    mb: 5
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, #ffffff 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '-15%',
          width: '30%',
          height: '50%',
          borderRadius: '40%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0)} 70%)`,
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '5%',
          right: '-10%',
          width: '25%',
          height: '40%',
          borderRadius: '40%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.light, 0.08)} 0%, ${alpha(theme.palette.secondary.light, 0)} 70%)`,
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={sectionTitleStyles}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={sectionSubtitleStyles}
            >
              OUR FLEET
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={sectionHeadingStyles}
            >
              Diverse Vehicle Options For Every Need
            </Typography>
            <Typography
              variant="body1"
              sx={sectionDescriptionStyles}
            >
              Choose from our diverse fleet of vehicles tailored to your needs and budget.
              From affordable standard options to executive sedans and premium luxury vehicles,
              we provide comfortable and reliable transportation for all occasions.
            </Typography>
          </motion.div>
        </Box>

        {/* Category filters */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: { xs: 5, md: 7 },
          }}
        >
          {categories.map((category, index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                onClick={() => setActiveCategory(category)}
                variant={activeCategory === category ? 'contained' : 'outlined'}
                color={activeCategory === category ? 'secondary' : 'primary'}
                sx={{
                  px: 3,
                  py: 1.2,
                  m: 0.5,
                  borderRadius: 10,
                  borderWidth: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: activeCategory === category ? 4 : 0,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }}
              >
                {category}
              </Button>
            </motion.div>
          ))}
        </Box>

        {/* Vehicle cards */}
        <GridContainer spacing={4}>
          {filteredVehicles.map((vehicle, index) => (
            <GridItem xs={12} sm={6} md={3} key={vehicle.id}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.7, 
                  delay: index * 0.15,
                  ease: "easeOut"
                }}
                viewport={{ once: true, margin: "-50px" }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden', height: '220px' }}>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.5 }}
                    >
                      <CardMedia
                        component="img"
                        height="220"
                        image={vehicle.image}
                        alt={vehicle.name}
                        sx={{ 
                          transition: 'transform 0.5s',
                          objectFit: 'cover',
                          objectPosition: 'center',
                        }}
                      />
                    </motion.div>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 2,
                      }}
                    >
                      <Chip 
                        label={vehicle.category} 
                        size="medium"
                        color="secondary"
                        sx={{ 
                          fontWeight: 600,
                          px: 1,
                          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)'
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.common.black, 0.75),
                        color: 'white',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.5, sm: 0.7 },
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                        minWidth: { xs: '90px', sm: '100px' },
                        textAlign: 'center',
                        lineHeight: 1.3,
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      <Box sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                        ₹{vehicle.totalPrice.toLocaleString('en-IN')}
                      </Box>
                      <Box sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, opacity: 0.9 }}>
                        Up to 300 KM
                      </Box>
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ 
                    p: { xs: 2, sm: 2.5, md: 3 }, 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: 0
                  }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ 
                        fontWeight: 700, 
                        mb: 2,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        pb: 1.5,
                        fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                        lineHeight: 1.3,
                        maxHeight: '2.6em',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-word',
                        wordWrap: 'break-word',
                        hyphens: 'auto',
                      }}
                    >
                      {vehicle.name}
                    </Typography>
                    
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 2.5,
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        flex: '1 1 calc(50% - 8px)',
                        minWidth: '140px'
                      }}>
                        <Box sx={{ 
                          mr: 1, 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          p: 0.7,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <PersonOutlineIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </Box>
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {vehicle.passengers} Passengers
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        flex: '1 1 calc(50% - 8px)',
                        minWidth: '140px'
                      }}>
                        <Box sx={{ 
                          mr: 1, 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          p: 0.7,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <WorkOutlineIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </Box>
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {vehicle.luggage} Luggage
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: 0.75,
                      mb: 2
                    }}>
                      {vehicle.amenities.map((amenity, idx) => (
                        <Chip
                          key={idx}
                          label={amenity}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderRadius: 1,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: '24px', sm: '28px' }
                          }}
                        />
                      ))}
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 1,
                      mb: 2,
                      pt: 1.5,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 0.75
                      }}>
                        <LocalOfferIcon 
                          fontSize="small" 
                          color="primary" 
                          sx={{ 
                            mt: 0.25,
                            flexShrink: 0
                          }} 
                        />
                        <Typography 
                          variant="caption" 
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            lineHeight: 1.4
                          }}
                        >
                          ₹{vehicle.hourlyRate}/HR (After 12 Hours)
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 0.75
                      }}>
                        <LocalOfferIcon 
                          fontSize="small" 
                          color="primary" 
                          sx={{ 
                            mt: 0.25,
                            flexShrink: 0
                          }} 
                        />
                        <Typography 
                          variant="caption" 
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            lineHeight: 1.4
                          }}
                        >
                          ₹{vehicle.perDayKm}/KM (After 300 KM)
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      mt: 'auto', 
                      display: 'flex', 
                      gap: { xs: 1, sm: 1.5 }, 
                      alignItems: 'center',
                      pt: 1
                    }} style={{ display: 'none' }}>
                      <motion.div
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        style={{ flex: 1 }}
                      >
                        <Button 
                          variant="outlined" 
                          size="medium" 
                          fullWidth
                          component={RouterLink}
                          to={`/fleet/${vehicle.id}`}
                          sx={{
                            borderRadius: 10,
                            py: 1,
                            borderWidth: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              borderWidth: 2,
                            }
                          }}
                        >
                          Details
                        </Button>
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        style={{ flex: 1 }}
                      >
                        <Button 
                          variant="contained" 
                          size="medium" 
                          color="secondary" 
                          fullWidth
                          component={RouterLink}
                          to={`/booking?vehicle=${vehicle.id}`}
                          endIcon={<DirectionsCarIcon />}
                          sx={{
                            borderRadius: 10,
                            py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          }}
                        >
                          Book
                        </Button>
                      </motion.div>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </GridItem>
          ))}
        </GridContainer>
        
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            textAlign: 'center',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              component={RouterLink}
              to="/fleet"
              variant="contained"
              color="primary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 5,
                py: 1.5,
                borderWidth: 2,
                fontWeight: 600,
                borderRadius: 30,
                textTransform: 'none',
                fontSize: '1.1rem',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              View All Vehicles
            </Button>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default FleetPreview; 